import { Fragment, useCallback, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronLeft, ChevronRight, Edit, Eye, PauseCircle, Plus, Search, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dropdown } from '@/components/ui/select'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { PageHeader } from '@/components/ui/page-header'
import { SoftwareForm } from '@/components/forms/SoftwareForm'
import { SoftwareAccountStatusModal } from '@/components/modals/SoftwareAccountStatusModal'
import { apiClient } from '@/lib/api-client'
import { accountStatusColor, accountStatusLabel, billingLabel, statusSummary } from '@/lib/software-format'
import { toast } from 'react-hot-toast'

export function SoftwarePage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchQuery = searchParams.get('search') ?? ''
  const statusFilter = searchParams.get('status') ?? 'all'
  const currentPage = Math.max(1, Number(searchParams.get('page') || '1') || 1)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [formOpen, setFormOpen] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [statusAccount, setStatusAccount] = useState<any>(null)

  const updateFilters = useCallback((patch: Record<string, string | number | undefined>) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === '' || value === 'all' || value === 1) next.delete(key)
        else next.set(key, String(value))
      })
      return next
    }, { replace: true })
  }, [setSearchParams])

  const { data, isLoading } = useQuery({
    queryKey: ['software', searchQuery, statusFilter, currentPage],
    queryFn: async () => {
      const params: Record<string, string | number> = { page: currentPage, limit: 12 }
      if (searchQuery.trim()) params.search = searchQuery.trim()
      if (statusFilter !== 'all') params.status = statusFilter
      return (await apiClient.get('/software', { params })).data
    },
  })

  const groups = data?.groups || []
  const pagination = data?.pagination
  const isExpanded = (id: number) => (searchQuery.trim() ? expanded[id] !== false : !!expanded[id])

  const deleteAccount = useMutation({
    mutationFn: async (accountId: number) => apiClient.delete(`/software/accounts/${accountId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['software'] })
      toast.success('Paid account deleted')
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Delete failed'),
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Software"
        actions={
          <Button onClick={() => { setEditId(null); setFormOpen(true) }}>
            <Plus className="mr-2 h-4 w-4" />
            Add software
          </Button>
        }
      />

      <div className="data-panel">
        <div className="flex flex-col gap-3 border-b border-border px-3 py-3 lg:flex-row lg:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(event) => updateFilters({ search: event.target.value, page: 1 })}
              placeholder="Search software, vendor, or account email"
              className="h-11 pl-9 lg:h-9"
            />
          </div>
          <Dropdown
            className="w-40"
            value={statusFilter}
            onChange={(value) => updateFilters({ status: value, page: 1 })}
            options={[
              { value: 'all', label: 'All status' },
              { value: 'active', label: 'Active' },
              { value: 'expired', label: 'Expired' },
              { value: 'cancelled', label: 'Stopped' },
            ]}
          />
        </div>

        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : groups.length === 0 ? (
          <div className="px-4 py-16 text-center">
            <p className="text-sm font-medium">No software found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Software</th>
                  <th>Billing</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group: any) => {
                  const open = isExpanded(group.id)
                  const accounts = group.accounts || []
                  const plans = Array.from(new Set(accounts.map((account: any) => account.plan).filter(Boolean)))
                  return (
                    <Fragment key={group.id}>
                      <tr
                        className="cursor-pointer"
                        onClick={() => setExpanded((current) => ({ ...current, [group.id]: !isExpanded(group.id) }))}
                      >
                        <td>
                          <div className="flex items-center gap-2">
                            <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`} />
                            <div className="min-w-0">
                              <p className="font-medium">{group.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {[group.vendor, `${accounts.length} account${accounts.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td data-label="Billing" className="text-sm text-muted-foreground">
                          {plans.length ? plans.join(', ') : '—'}
                        </td>
                        <td data-label="Status" className="text-xs text-muted-foreground">{statusSummary(accounts)}</td>
                        <td className="is-actions" onClick={(event) => event.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-8" onClick={() => { setEditId(group.id); setFormOpen(true) }}>
                              <Edit className="mr-1.5 h-3.5 w-3.5" />
                              Edit
                            </Button>
                            <Button asChild variant="ghost" size="sm" className="h-8">
                              <Link to={`/software/${group.id}`}>Open</Link>
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {open && accounts.map((account: any) => (
                        <tr key={account.id} className="is-child bg-muted/30">
                          <td>
                            <div className="min-w-0 pl-6">
                              <Link to={`/software/accounts/${account.id}`} className="text-sm font-medium text-primary hover:underline">
                                {account.accountName}
                              </Link>
                              <p className="truncate text-xs text-muted-foreground">{account.email}</p>
                            </div>
                          </td>
                          <td data-label="Billing">
                            <p className="text-sm">{billingLabel(account)}</p>
                            <p className="text-xs text-muted-foreground">
                              {account.memberCount || 0} user{(account.memberCount || 0) === 1 ? '' : 's'}
                            </p>
                          </td>
                          <td data-label="Status">
                            <Badge className={accountStatusColor(account.status)}>{accountStatusLabel(account.status)}</Badge>
                            {account.statusReason && (
                              <p className="mt-1 max-w-[16rem] text-xs text-muted-foreground">{account.statusReason}</p>
                            )}
                          </td>
                          <td className="is-actions">
                            <div className="flex justify-end gap-1">
                              <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                                <Link to={`/software/accounts/${account.id}`} aria-label="View account">
                                  <Eye className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Update status" title="Update status" onClick={() => setStatusAccount(account)}>
                                <PauseCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                aria-label="Delete account"
                                onClick={() => {
                                  if (window.confirm(`Delete paid account ${account.email}?`)) deleteAccount.mutate(account.id)
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {pagination && (
          <div className="flex items-center justify-between border-t border-border px-3 py-2 text-sm text-muted-foreground">
            <span>
              {pagination.totalItems} software · {pagination.totalAccounts ?? 0} accounts · page {pagination.currentPage} of {pagination.totalPages || 1}
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" className="h-8" disabled={!pagination.hasPrevPage} onClick={() => updateFilters({ page: pagination.currentPage - 1 })}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-8" disabled={!pagination.hasNextPage} onClick={() => updateFilters({ page: pagination.currentPage + 1 })}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <SoftwareForm
        isOpen={formOpen}
        softwareId={editId}
        onClose={() => { setFormOpen(false); setEditId(null) }}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['software'] })}
      />
      <SoftwareAccountStatusModal
        isOpen={!!statusAccount}
        account={statusAccount}
        onClose={() => setStatusAccount(null)}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['software'] })}
      />
    </div>
  )
}
